import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { forOwn, get } from 'lodash-es';
import dateFormat from 'dateformat';
import { connect } from 'react-redux';
import { Form } from 'reactstrap';
import { FORM_VALIDATORS } from 'site-modules/dealerships/constants/dealer-review';
import { formValidation } from 'site-modules/shared/components/form-validation/form-validation';
import { REVIEW_TITLE, REVIEWS_SALES } from 'client/constants/dealer-reviews';
import { EdmundsAPI } from 'client/data/api/api-client';
import { getCurrentDate } from 'site-modules/shared/utils/date-utils';
import { ApiMetrics, getMetricApiOptions, withMetrics } from 'client/utils/metrics-hoc';
import { logger } from 'client/utils/logger/logger';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { VisitorModel } from 'client/data/models/visitor';
import { DealerReviewFormFields } from './dealer-review-form-fields';

import './dealer-review-form.scss';

const YES_ANSWER = 'YES';

export class DealerReviewFormUI extends Component {
  static propTypes = {
    sessionId: PropTypes.string.isRequired,
    visitorId: PropTypes.string.isRequired,
    userAgent: PropTypes.string.isRequired,
    fields: PropTypes.shape({}).isRequired,
    fieldRef: PropTypes.func.isRequired,
    validate: PropTypes.func.isRequired,
    validationErrors: PropTypes.shape({}),
    dealerName: PropTypes.string.isRequired,
    stateCode: PropTypes.string.isRequired,
    dealerId: PropTypes.number.isRequired,
    displayThankYou: PropTypes.func.isRequired,
    reviewType: PropTypes.string.isRequired,
    apiMetrics: ApiMetrics.isRequired,
    previousReviewText: PropTypes.string,
    previousAuthorName: PropTypes.string,
    previousAuthorEmail: PropTypes.string,
    isUpdate: PropTypes.bool,
    reviewId: PropTypes.string,
  };

  static defaultProps = {
    validationErrors: {},
    previousReviewText: undefined,
    previousAuthorName: undefined,
    previousAuthorEmail: undefined,
    isUpdate: false,
    reviewId: '',
  };

  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      showOptionErrors: false,
      isSubmitFailed: false,
    };
  }

  onClick = (event, optionsValues) => {
    event.preventDefault();

    const { validate } = this.props;

    if ((validate && !validate()) || !this.isOptionFieldsValid(optionsValues)) {
      this.setState({ showOptionErrors: true });
    } else {
      this.setState({ isLoading: true });

      const fieldValues = {};

      forOwn(this.props.fields, ({ value }, name) => {
        fieldValues[name] = value();
      });

      this.onSubmit({ ...fieldValues, ...optionsValues });
    }
  };

  onSubmit = async values => {
    const {
      stateCode,
      reviewType,
      dealerId,
      sessionId,
      visitorId,
      userAgent,
      previousReviewText,
      previousAuthorName,
      previousAuthorEmail,
      apiMetrics,
      reviewId,
      isUpdate,
    } = this.props;
    const dealerReviewsApi = isUpdate ? '/dealerreviews/v2/update' : '/dealerreviews/v2/';
    const { title, text, userRating, authorName, email, recommendValue } = values;
    const updatedText = previousReviewText
      ? `${previousReviewText}\n\nUpdate ${dateFormat(getCurrentDate(), 'mmmm yyyy')}:\n${text}`
      : text;
    const commonParams = {
      dealerId,
      stateCode,
      publishingSource: 'EDMUNDS_WEB_SITE',
      authorOrganization: 'Edmunds.com',
      moderationStatus: 'pending',
      emailReviewActivation: true,
      sessionId,
      visitorId,
      userAgent,
      title,
      text: updatedText,
      authorName: authorName || previousAuthorName,
      email: email || previousAuthorEmail,
      overallRating: userRating,
      reviewType,
      dealerRecommended: recommendValue.toUpperCase() === YES_ANSWER,
      ...(isUpdate ? { id: reviewId } : {}),
    };
    let bodyParams;

    if (reviewType === REVIEWS_SALES) {
      const { purchaseValue } = values;

      bodyParams = {
        ...commonParams,
        carBought: purchaseValue.toUpperCase() === YES_ANSWER,
      };
    } else {
      const { satisfiedValue, completeTheWorkValue, commitmentsValue, quoteOfTheWorkValue, fairPriceValue } = values;

      bodyParams = {
        ...commonParams,
        workQualitySatisfied: satisfiedValue.toUpperCase(),
        workCompleteOnTime: completeTheWorkValue.toUpperCase(),
        priceOfServiceFair: fairPriceValue.toUpperCase(),
        workQuoteProvided: quoteOfTheWorkValue.toUpperCase(),
        allCommitmentsMade: commitmentsValue.toUpperCase(),
      };
    }

    const options = {
      method: 'POST',
      headers: {
        ...getMetricApiOptions(apiMetrics).headers,
        'content-type': 'application/json',
      },
      body: JSON.stringify(bodyParams),
    };

    try {
      const response = await EdmundsAPI.fetchJson(dealerReviewsApi, options);
      if (response.id) {
        this.props.displayThankYou();
      }
    } catch (error) {
      logger.error(`Dealer review submit error - ${error}`);
      this.setState({ isLoading: false, isSubmitFailed: true });
    }
  };

  isOptionFieldsValid = optionsValues => {
    if (this.props.reviewType === REVIEWS_SALES) {
      const { recommendValue, purchaseValue } = optionsValues;
      return !!(recommendValue && purchaseValue);
    }
    const {
      recommendValue,
      satisfiedValue,
      completeTheWorkValue,
      commitmentsValue,
      quoteOfTheWorkValue,
      fairPriceValue,
    } = optionsValues;
    return !!(
      recommendValue &&
      satisfiedValue &&
      completeTheWorkValue &&
      commitmentsValue &&
      quoteOfTheWorkValue &&
      fairPriceValue
    );
  };

  componentFieldRef = field => {
    if (!field) {
      return undefined;
    }
    return this.props.fieldRef(field.props.name, field);
  };

  render() {
    const { validate, validationErrors, reviewType, dealerName, isUpdate } = this.props;
    const { isLoading, showOptionErrors, isSubmitFailed } = this.state;
    const reviewTitle = REVIEW_TITLE[reviewType];

    return (
      <Form id="dealer-review-form" className="dealer-review-form" data-test="dealer-form">
        <h2 className="heading-3 mb-1_5">{isUpdate ? 'Update Your Review' : `Write a ${reviewType} review`}</h2>
        <p>
          Thank you for choosing to write a review about your {reviewType} experience at {dealerName}. Your review will
          help other consumers make purchasing decisions.
        </p>
        <div className="mb-1_5">
          <div className="d-flex justify-content-between align-items-baseline">
            <span className="heading-4">{reviewTitle} Rating</span>
            <span className="medium">*Required fields</span>
          </div>
          {isSubmitFailed && (
            <div className="text-danger fw-bold mt-0_5">Review submission failed. Please submit your review again.</div>
          )}
        </div>
        <DealerReviewFormFields
          fieldRef={this.componentFieldRef}
          validate={validate}
          validationErrors={validationErrors}
          showOptionErrors={showOptionErrors}
          reviewType={reviewType}
          onSubmit={this.onClick}
          isLoading={isLoading}
          isUpdate={isUpdate}
        />
      </Form>
    );
  }
}

export const stateToPropsConfig = {
  visitorId: bindToPath('id', VisitorModel),
  sessionId: bindToPath('session', VisitorModel),
};
export const mapStateToProps = state => ({
  userAgent: get(state, 'ua.userAgent'),
});

export const DealerReviewForm = connect(mapStateToProps)(
  connectToModel(formValidation(withMetrics(DealerReviewFormUI), FORM_VALIDATORS), stateToPropsConfig)
);
